import { axiosApiInstance } from '@/utils/axios';
import config from '../config';
import { get, post, put, patch, del } from '../utils/request';

const WorkpackController = {
  // Workpack CRUD

  // get all workpacks
  getWorkpacks(inspectionId) {
    return get(`${config.apiPath}/v1/inspections/${inspectionId}/workpacks`);
  },

  async getWorkpacksByState(state, inspectionId) {
    const workpacks = await this.getWorkpacks(inspectionId);
    return workpacks.filter((w) => w.state === state);
  },

  // get one workpack
  getWorkpack(id, inspectionId) {
    return get(`${config.apiPath}/v1/inspections/${inspectionId}/workpacks/${id}`);
  },

  // create one workpack
  createWorkpack(workpack, inspectionId) {
    return post(`${config.apiPath}/v1/inspections/${inspectionId}/workpacks`, workpack);
  },

  // update one workpack
  putWorkpack(id, workpack, inspectionId) {
    return put(`${config.apiPath}/v1/inspections/${inspectionId}/workpacks/${id}`, workpack);
  },

  // patch one workpack
  updateWorkpack(id, workpack, inspectionId) {
    return patch(`${config.apiPath}/v1/inspections/${inspectionId}/workpacks/${id}`, workpack);
  },

  // delete one workpack
  deleteWorkpack(id, inspectionId) {
    return del(`${config.apiPath}/v1/inspections/${inspectionId}/workpacks/${id}`);
  },

  // Workpack logs CRUD

  // get all workpack logs
  getWorkpackLogs(id, inspectionId) {
    return get(`${config.apiPath}/v1/inspections/${inspectionId}/workpacks/${id}/logs`);
  },

  // get one workpack log
  getWorkpackLog(id, logId, inspectionId) {
    return get(`${config.apiPath}/v1/inspections/${inspectionId}/workpacks/${id}/logs/${logId}`);
  },

  // get all workpack logs
  createWorkpackLog(id, log, inspectionId) {
    return post(`${config.apiPath}/v1/inspections/${inspectionId}/workpacks/${id}/logs`, log);
  },

  // update one workpack log
  putWorkpackLog(id, logId, update, inspectionId) {
    return put(`${config.apiPath}/v1/inspections/${inspectionId}/workpacks/${id}/logs/${logId}`, update);
  },

  // patch one workpack log
  updateWorkpackLog(id, logId, update, inspectionId) {
    return patch(`${config.apiPath}/v1/inspections/${inspectionId}/workpacks/${id}/logs/${logId}`, update);
  },

  // delete one workpack log
  deleteWorkpackLog(id, logId, inspectionId) {
    return del(`${config.apiPath}/v1/inspections/${inspectionId}/workpacks/${id}/logs/${logId}`);
  },

  // Workpack items CRUD

  // get all workpack items
  getWorkpackItems(id, inspectionId) {
    return get(`${config.apiPath}/v1/inspections/${inspectionId}/workpacks/${id}/items`);
  },

  exportWorkpackItems(id, items, inspectionId) {
    return axiosApiInstance.post(
      `${config.apiPath}/v1/inspections/${inspectionId}/workpacks/${id}/items/export`,
      { items },
      { responseType: 'blob' }
    );
  },

  transferWorkpackItems(id, destinationWorkpackId, items, inspectionId) {
    return patch(`${config.apiPath}/v1/inspections/${inspectionId}/workpacks/${id}/items/copy`, {
      destinationWorkpackId,
      items,
    });
  },

  // get one workpack item
  getWorkpackItem(id, itemId, inspectionId) {
    return get(`${config.apiPath}/v1/inspections/${inspectionId}/workpacks/${id}/items/${itemId}`);
  },

  // create one workpack item
  createWorkpackItem(id, item, inspectionId) {
    return post(`${config.apiPath}/v1/inspections/${inspectionId}/workpacks/${id}/items`, item);
  },

  // update one workpack item
  putWorkpackItem(id, itemId, update, inspectionId) {
    return put(`${config.apiPath}/v1/inspections/${inspectionId}/workpacks/${id}/items/${itemId}`, update);
  },

  // patch one workpack item
  updateWorkpackItem(id, itemId, update, inspectionId) {
    return patch(`${config.apiPath}/v1/inspections/${inspectionId}/workpacks/${id}/items/${itemId}`, update);
  },

  // delete one workpack item
  deleteWorkpackItem(id, itemId, inspectionId) {
    return del(`${config.apiPath}/v1/inspections/${inspectionId}/workpacks/${id}/items/${itemId}`);
  },

  // Item Parts CRUD

  // get all item parts
  getItemParts(id, itemId, inspectionId) {
    return get(`${config.apiPath}/v1/inspections/${inspectionId}/workpacks/${id}/items/${itemId}/parts`);
  },

  transferItemParts(id, destinationWorkpackId, itemId, parts, inspectionId) {
    return patch(`${config.apiPath}/v1/inspections/${inspectionId}/workpacks/${id}/items/${itemId}/parts/copy`, {
      destinationWorkpackId,
      parts,
    });
  },

  closeoutItemParts(id, itemId, parts, inspectionId) {
    return patch(`${config.apiPath}/v1/inspections/${inspectionId}/workpacks/${id}/items/${itemId}/parts/closeout`, {
      parts,
    });
  },

  // get one item part
  getItemPart(id, itemId, partId, inspectionId) {
    return get(`${config.apiPath}/v1/inspections/${inspectionId}/workpacks/${id}/items/${itemId}/parts/${partId}`);
  },

  // create one item part
  createItemPart(id, itemId, part, inspectionId) {
    return post(`${config.apiPath}/v1/inspections/${inspectionId}/workpacks/${id}/items/${itemId}/parts`, part);
  },

  // update one item part
  putItemPart(id, itemId, partId, update, inspectionId) {
    return put(
      `${config.apiPath}/v1/inspections/${inspectionId}/workpacks/${id}/items/${itemId}/parts/${partId}`,
      update
    );
  },

  // patch one item part
  updateItemPart(id, itemId, partId, update, inspectionId) {
    return patch(
      `${config.apiPath}/v1/inspections/${inspectionId}/workpacks/${id}/items/${itemId}/parts/${partId}`,
      update
    );
  },

  // delete one item part
  deleteItemPart(id, itemId, partId, inspectionId) {
    return del(`${config.apiPath}/v1/inspections/${inspectionId}/workpacks/${id}/items/${itemId}/parts/${partId}`);
  },

  // Part Views CRUD

  // read all part views
  getPartViews(id, itemId, partId, inspectionId) {
    return get(
      `${config.apiPath}/v1/inspections/${inspectionId}/workpacks/${id}/items/${itemId}/parts/${partId}/views`
    );
  },

  transferPartViews(id, destinationWorkpackId, itemId, partId, views, inspectionId) {
    return patch(
      `${config.apiPath}/v1/inspections/${inspectionId}/workpacks/${id}/items/${itemId}/parts/${partId}/views/copy`,
      { destinationWorkpackId, views }
    );
  },

  // read one part view
  getPartView(id, itemId, partId, viewId, inspectionId) {
    return get(
      `${config.apiPath}/v1/inspections/${inspectionId}/workpacks/${id}/items/${itemId}/parts/${partId}/views/${viewId}`
    );
  },

  // create one part view
  createPartView(id, itemId, partId, view, inspectionId) {
    return post(
      `${config.apiPath}/v1/inspections/${inspectionId}/workpacks/${id}/items/${itemId}/parts/${partId}/views`,
      view
    );
  },

  // update one part view
  putPartView(id, itemId, partId, viewId, update, inspectionId) {
    return put(
      `${config.apiPath}/v1/inspections/${inspectionId}/workpacks/${id}/items/${itemId}/parts/${partId}/views/${viewId}`,
      update
    );
  },

  // patch one part view
  updatePartView(id, itemId, partId, viewId, update, inspectionId) {
    return patch(
      `${config.apiPath}/v1/inspections/${inspectionId}/workpacks/${id}/items/${itemId}/parts/${partId}/views/${viewId}`,
      update
    );
  },

  // delete one part view
  deletePartView(id, itemId, partId, viewId, inspectionId) {
    return del(
      `${config.apiPath}/v1/inspections/${inspectionId}/workpacks/${id}/items/${itemId}/parts/${partId}/views/${viewId}`
    );
  },

  // create/update workpack item part view
  createWorkpackItemPartView(id, update, inspectionId) {
    return post(`${config.apiPath}/v1/inspections/${inspectionId}/workpacks/${id}/views`, update);
  },

  getWorkpacksStats(inspectionId) {
    return get(`${config.apiPath}/v1/inspections/${inspectionId}/workpacks/stats`);
  },
};

export default WorkpackController;
